import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import App from './App.tsx';
import { Login } from './pages/Login.tsx';
import { ProtectedRoute } from './components/ProtectedRoute.tsx';
import { LanguageProvider } from './lib/LanguageContext.tsx';

// Page imports
import { Dashboard } from './pages/Dashboard.tsx';
import { Programs } from './pages/Programs.tsx';
import { Registration } from './pages/Registration.tsx';
import { PublicProgramRegistration } from './pages/PublicProgramRegistration.tsx';
import { ProgramDetail } from './pages/ProgramDetail.tsx';
import { Materials } from './pages/Materials.tsx';
import { MaterialDetail } from './pages/MaterialDetail.tsx';
import { Evaluations } from './pages/Evaluations.tsx';
import { EvaluationDetail } from './pages/EvaluationDetail.tsx';
import { Results } from './pages/Results.tsx';
import { Certificates } from './pages/Certificates.tsx';
import { Profile } from './pages/Profile.tsx';
import { Receipt } from './pages/Receipt.tsx';
import { SignUp } from './pages/SignUp.tsx';
import { ReflectionDetail } from './pages/ReflectionDetail.tsx';

import { VerifyEmail } from './pages/VerifyEmail.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { Transcripts } from './pages/Transcripts.tsx';
import { LegacyTranscripts } from './pages/LegacyTranscripts.tsx';
import { Announcements } from './pages/Announcements.tsx';

import { ErrorBoundary } from './components/ErrorBoundary.tsx';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<SignUp />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/daftar/:code" element={<PublicProgramRegistration />} />
              <Route
                element={
                  <ProtectedRoute>
                    <App />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/programs" element={<Programs />} />
                <Route path="/programs/:id/register" element={<Registration />} />
                <Route path="/programs/:id" element={<ProgramDetail />} />
                <Route path="/programs/:programId/materials/:id" element={<MaterialDetail />} />
                <Route path="/programs/:programId/reflection" element={<ReflectionDetail />} />
                <Route path="/programs/:programId/results" element={<Results />} />
                <Route path="/materials" element={<Materials />} />
                <Route path="/materials/:id" element={<MaterialDetail />} />
                <Route path="/evaluations" element={<Evaluations />} />
                <Route path="/evaluations/:id" element={<EvaluationDetail />} />
                <Route path="/results" element={<Results />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/receipt/:id" element={<Receipt />} />
                <Route path="/certificates" element={<Certificates />} />
                <Route path="/transcripts" element={<Transcripts />} />
                <Route path="/legacy-transcripts" element={<LegacyTranscripts />} />
                <Route path="/announcements" element={<Announcements />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </LanguageProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
